import React, { useEffect, useState } from 'react';

import Icon from 'views/site/Icon.js';

import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * Header
 *
 * Shared chrome for the whole site, marketing views and legacy panels alike.
 * It replaces the dealership colored band the replicated website used to
 * carry: the storefront's white bar reads as a product company rather than a
 * brochure, and the dealership's color now shows where it converts — on the
 * call button — instead of as a field of paint behind everything.
 *
 * Navigation items are supplied by App rather than defined here, so the one
 * place that knows which views a dealership has enabled (Google reviews, for
 * instance) stays the one place that decides.
 *
 * Styled entirely inline, like the rest of this repository, so it depends on
 * no stylesheet rules of its own. The `site` class is present only so the
 * quote badge picks up the brand custom property.
 */

const Header = ({ account, activeParams, activeRoute, dealership, items, onNavigate, onQuoteClick, onSignOut, quoteCount }) => {

    const [expanded, setExpanded] = useState(null);
    const [hovered, setHovered] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    /*
     * Whether a menu entry is the page currently being looked at.
     *
     * Every product entry carries the key 'products', because they all route to
     * the same view — so matching on the key alone lit all six of them the
     * moment any product page opened. The handle is what actually distinguishes
     * them, so it has to be part of the comparison. Entries without a handle
     * (the parent, the legal items) compare null to null and behave as before.
     */
    const isActive = item => {

        if(item.key !== activeRoute) {
            return false;
        }

        // An entry that names a specific product lights only on that product.
        if(item.params?.handle) {
            return item.params.handle === (activeParams?.handle || null);
        }

        // Same for a section of the embedded app. Every one of its entries
        // carries key 'app', so without this the whole menu lit up the moment
        // the app opened.
        if(item.params?.section) {
            return item.params.section === (activeParams?.section || null);
        }

        // An entry that names no product — the section parent, the legal
        // links — lights for the whole section, so "Products" stays marked
        // while you read any one of them.
        return true;
    }

    const onItemClick = item => {
        setExpanded(null);
        setMenuOpen(false);
        if(typeof(item.onClick) === 'function') {
            item.onClick();
            return;
        }
        if(onNavigate) {
            onNavigate(item);
        }
    }

    const onAccountItemClick = item => {
        setExpanded(null);
        setMenuOpen(false);
        if(item.key === 'sign_out') {
            if(typeof(onSignOut) === 'function') {
                onSignOut();
            }
            return;
        }
        onItemClick(item);
    }

    /*
     * The homeowner's account.
     *
     * An avatar rather than a worded nav item, because what is behind it is not
     * another page of the storefront - it is the visitor's own system, and the
     * sections inside it only exist once they have signed in. Sitting with the
     * phone and quote controls rather than among Home / Products / Support says
     * that without needing a label to explain it.
     *
     * The sections come from the app, which is the only thing that knows which
     * ones this network grants. Signed out there are none, and the control is a
     * plain way in.
     */
    const getAccount = () => {

        let sections = account?.sections || [];
        let authenticated = account?.authenticated === true;
        let open = expanded === 'account';

        return (
            <div
            onMouseEnter={setExpanded.bind(this, 'account')}
            onMouseLeave={setExpanded.bind(this, null)}
            style={{
                position: 'relative'
            }}>
                <div
                aria-label={authenticated ? 'Your account' : 'Sign in'}
                onClick={onAccountItemClick.bind(this, { key: 'app', view: 'app' })}
                role={'button'}
                style={{
                    alignItems: 'center',
                    backgroundColor: Theme.colors.primary(),
                    border: `1px solid ${Theme.colors.primary()}`,
                    borderRadius: 20,
                    cursor: 'pointer',
                    display: 'flex',
                    height: 40,
                    justifyContent: 'center',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    width: 40
                }}>
                    <Icon
                    name={'person-fill'}
                    size={16}
                    color={Theme.colors.white} />
                </div>

                {open && authenticated && sections.length > 0 && (
                    <div style={{
                        paddingTop: 14,
                        position: 'absolute',
                        right: 0,
                        top: '100%'
                    }}>
                        <div style={{
                            backgroundColor: Theme.colors.white,
                            border: `1px solid ${Theme.colors.border()}`,
                            borderRadius: 16,
                            boxShadow: Theme.boxShadow.cardHover,
                            minWidth: 240,
                            padding: 8
                        }}>
                            {sections.map((section, index) => (
                                <div
                                key={index}
                                onClick={onAccountItemClick.bind(this, {
                                    key: 'app',
                                    params: { section: section.key },
                                    view: 'app'
                                })}
                                onMouseEnter={setHovered.bind(this, `account-${index}`)}
                                onMouseLeave={setHovered.bind(this, null)}
                                style={{
                                    backgroundColor: hovered === `account-${index}` ? Theme.colors.offWhite : 'transparent',
                                    borderRadius: 10,
                                    cursor: 'pointer',
                                    padding: '11px 14px',
                                    transition: 'background-color 0.15s ease'
                                }}>
                                    <div style={{
                                        ...Theme.textStyles.navLink({ fontSize: 15, fontWeight: 600 }),
                                        ...activeParams?.section === section.key && { color: Theme.colors.primary() }
                                    }}>{section.label}</div>
                                </div>
                            ))}

                            {/* the rule marks the shift from moving around the
                                account to leaving it */}
                            <div style={{
                                backgroundColor: Theme.colors.border(),
                                height: 1,
                                margin: '6px 8px'
                            }} />

                            <div
                            onClick={onAccountItemClick.bind(this, { key: 'sign_out' })}
                            onMouseEnter={setHovered.bind(this, 'account-sign-out')}
                            onMouseLeave={setHovered.bind(this, null)}
                            style={{
                                alignItems: 'center',
                                backgroundColor: hovered === 'account-sign-out' ? Theme.colors.offWhite : 'transparent',
                                borderRadius: 10,
                                cursor: 'pointer',
                                display: 'flex',
                                gap: 10,
                                padding: '11px 14px',
                                transition: 'background-color 0.15s ease'
                            }}>
                                <Icon name={'door-closed-fill'} size={13} color={Theme.colors.subText()} />
                                <div style={Theme.textStyles.navLink({ fontSize: 15, fontWeight: 600 })}>{'Log Out'}</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const onPhoneClick = () => {
        setMenuOpen(false);
        window.open(`tel://${dealership.phone_number}`);
    }

    const getLogo = () => {
        return (
            <div
            onClick={onItemClick.bind(this, { key: 'home', view: 'home', waypoint: 'home' })}
            style={{
                alignItems: 'center',
                cursor: 'pointer',
                display: 'flex',
                gap: 10,
                justifySelf: 'start',
                minWidth: 0
            }}>
                <img
                alt={dealership.name}
                src={dealership.logo?.url || 'images/dealership-replicated-logo-placeholder.png'}
                style={{
                    borderRadius: 8,
                    flexShrink: 0,
                    height: 40,
                    objectFit: 'contain',
                    width: 40
                }} />
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    textAlign: 'left'
                }}>
                    <span style={{
                        ...Theme.textStyles.subHeading({ fontSize: 16 }),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>{dealership.name}</span>
                    {dealership.locality && dealership.administrative_area_level_1 && (
                        <span style={Theme.textStyles.caption({ color: 'rgb(110, 110, 115)' })}>
                            {`${dealership.locality}, ${dealership.administrative_area_level_1}`}
                        </span>
                    )}
                </div>
            </div>
        )
    }

    const getNavigation = () => {

        if(Utils.isMobile() === true) {
            // Holds the middle column so the actions stay in the right column
            return <div />;
        }

        return (
            <nav style={{
                alignItems: 'center',
                display: 'flex',
                gap: 28
            }}>
                {items.map((item, index) => {

                    let active = isActive(item);

                    // A parent with children opens on hover, like the products
                    // menu it will eventually also carry
                    if(item.items?.length > 0) {
                        return (
                            <div
                            key={index}
                            onMouseEnter={setExpanded.bind(this, item.key)}
                            onMouseLeave={setExpanded.bind(this, null)}
                            style={{
                                position: 'relative'
                            }}>
                                {/* The parent is still a destination — hovering
                                    opens the menu, clicking goes to the page. */}
                                <span
                                onClick={item.view ? onItemClick.bind(this, item) : null}
                                style={Theme.textStyles.navLink({
                                    alignItems: 'center',
                                    cursor: item.view ? 'pointer' : 'default',
                                    display: 'inline-flex',
                                    gap: 6,
                                    ...active && { color: Theme.colors.primary() }
                                })}>
                                    {item.title}
                                    <Icon
                                    name={'chevron-down'}
                                    size={11}
                                    style={{
                                        opacity: 0.55,
                                        transform: expanded === item.key ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease'
                                    }} />
                                </span>
                                {expanded === item.key && (
                                    <div style={{
                                        left: -16,
                                        paddingTop: 14,
                                        position: 'absolute',
                                        top: '100%'
                                    }}>
                                        <div style={{
                                            backgroundColor: Theme.colors.white,
                                            border: `1px solid ${Theme.colors.border()}`,
                                            borderRadius: 16,
                                            boxShadow: Theme.boxShadow.cardHover,
                                            minWidth: 280,
                                            padding: 8
                                        }}>
                                            {item.items.map((subItem, subIndex) => (
                                                <React.Fragment key={subIndex}>
                                                    {/* a divider marks the shift from
                                                        browsing to doing */}
                                                    {subItem.divided === true && (
                                                        <div style={{
                                                            backgroundColor: Theme.colors.border(),
                                                            height: 1,
                                                            margin: '6px 8px'
                                                        }} />
                                                    )}
                                                    <div
                                                    onClick={onItemClick.bind(this, subItem)}
                                                    onMouseEnter={setHovered.bind(this, `${item.key}-${subIndex}`)}
                                                    onMouseLeave={setHovered.bind(this, null)}
                                                    style={{
                                                        backgroundColor: hovered === `${item.key}-${subIndex}` ? Theme.colors.offWhite : 'transparent',
                                                        borderRadius: 10,
                                                        cursor: 'pointer',
                                                        padding: '11px 14px',
                                                        transition: 'background-color 0.15s ease'
                                                    }}>
                                                        <div style={{
                                                            ...Theme.textStyles.navLink({ fontSize: 15, fontWeight: 600 }),
                                                            marginBottom: subItem.subtitle ? 2 : 0,
                                                            ...subItem.accent === true && { color: Theme.colors.primary() },
                                                            ...isActive(subItem) && !subItem.accent && { color: Theme.colors.primary() }
                                                        }}>{subItem.title}</div>
                                                        {subItem.subtitle && (
                                                            <div style={Theme.textStyles.caption({ color: Theme.colors.subText() })}>
                                                                {subItem.subtitle}
                                                            </div>
                                                        )}
                                                    </div>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    }

                    return (
                        <span
                        key={index}
                        onClick={onItemClick.bind(this, item)}
                        style={Theme.textStyles.navLink({
                            ...active && { color: Theme.colors.primary() }
                        })}>{item.title}</span>
                    )
                })}
            </nav>
        )
    }

    const getActions = () => {
        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                gap: 14,
                justifySelf: 'end'
            }}>
                {dealership.phone_number && Utils.isMobile() === false && (
                    <button
                    onClick={onPhoneClick}
                    style={{
                        ...Theme.styles.button.primary(),
                        fontSize: 14,
                        padding: '10px 20px'
                    }}>
                        <Icon name={'telephone-fill'} size={12} />
                        {Utils.formatPhoneNumber(dealership.phone_number)}
                    </button>
                )}

                {/* Nothing to show until something has been added to it. An
                    empty container is a control that does nothing, and the
                    badge that explains it only exists once there is a count. */}
                {quoteCount > 0 && (
                    <div
                    aria-label={'Your network'}
                    className={'site'}
                    onClick={onQuoteClick}
                    style={{
                        alignItems: 'center',
                        backgroundColor: Theme.colors.dark(),
                        borderRadius: 20,
                        cursor: 'pointer',
                        display: 'flex',
                        height: 40,
                        justifyContent: 'center',
                        position: 'relative',
                        transition: 'transform 0.2s ease',
                        width: 40
                    }}>
                        {/* a house rather than a clipboard: what is being
                            assembled is the network for a home, not paperwork */}
                        <Icon name={'house-door-fill'} size={15} color={Theme.colors.white} />
                        <div className={'cart-badge'}>{quoteCount}</div>
                    </div>
                )}

                {getAccount()}

                {Utils.isMobile() === true && (
                    <div
                    onClick={setMenuOpen.bind(this, !menuOpen)}
                    style={{
                        alignItems: 'center',
                        cursor: 'pointer',
                        display: 'flex',
                        height: 40,
                        justifyContent: 'center',
                        width: 32
                    }}>
                        <Icon
                        name={menuOpen ? 'x-lg' : 'list'}
                        size={20} />
                    </div>
                )}
            </div>
        )
    }

    const getMobileMenu = () => {

        if(Utils.isMobile() === false || menuOpen === false) {
            return null;
        }

        /*
         * Children belong to their parent, not to the top level.
         *
         * These used to be flattened, on the reasoning that the legal group was
         * the only parent and hiding two links behind a disclosure cost more
         * than it saved. That was true when it was written. It stopped being
         * true when the products menu gained the whole catalogue: flattening now
         * produces one undifferentiated list where Smoke Sensor and Contact sit
         * at the same level, and nothing tells you which of them is a product.
         *
         * So the parent stays a row of its own and its children sit beneath it,
         * indented and quieter. Still one tap to anything - the original goal -
         * but the shape of the menu now matches the shape of the site.
         */

        return (
            <div style={{
                backgroundColor: 'rgba(255,255,255,0.98)',
                backdropFilter: 'blur(20px)',
                borderBottom: `1px solid ${Theme.colors.border()}`,
                left: 0,
                padding: '8px 20px 20px',
                position: 'fixed',
                right: 0,
                top: Theme.layout.headerHeight(),
                zIndex: 4999
            }}>
                {items.map((item, index) => (
                    <React.Fragment key={index}>
                        {/* A parent opens and closes rather than leading
                            anywhere. Collapsed to start, so the menu opens as a
                            list of sections rather than a wall of every link on
                            the site. The chevron turns to say which it is. */}
                        <div
                        className={'pressable'}
                        onClick={item.items?.length > 0
                            ? () => setExpanded(current => current === item.key ? null : item.key)
                            : onItemClick.bind(this, item)}
                        style={{
                            alignItems: 'center',
                            borderBottom: `1px solid ${Theme.colors.border()}`,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '14px 0',
                            ...Theme.textStyles.navLink({ fontSize: 16 }),
                            ...isActive(item) && { color: Theme.colors.primary() }
                        }}>
                            {item.title}
                            {item.items?.length > 0 && (
                                <Icon
                                name={'chevron-down'}
                                size={11}
                                style={{
                                    opacity: 0.55,
                                    transform: expanded === item.key ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease'
                                }} />
                            )}
                        </div>

                        {item.items?.length > 0 && expanded === item.key && (
                            <div style={{
                                borderBottom: `1px solid ${Theme.colors.border()}`,
                                paddingBottom: 8
                            }}>
                                {item.items.map((subItem, subIndex) => (
                                    <div
                                    className={'pressable'}
                                    key={subIndex}
                                    onClick={onItemClick.bind(this, subItem)}
                                    style={{
                                        cursor: 'pointer',
                                        padding: '11px 0 11px 16px',
                                        ...Theme.textStyles.navLink({ color: Theme.colors.subText(), fontSize: 15 }),
                                        ...subItem.accent === true && { color: Theme.colors.primary() },
                                        ...isActive(subItem) && { color: Theme.colors.primary() }
                                    }}>{subItem.title}</div>
                                ))}
                            </div>
                        )}
                    </React.Fragment>
                ))}
                {dealership.phone_number && (
                    <button
                    onClick={onPhoneClick}
                    style={{
                        ...Theme.styles.button.primary(),
                        marginTop: 18,
                        width: '100%'
                    }}>
                        <Icon name={'telephone-fill'} size={12} />
                        {Utils.formatPhoneNumber(dealership.phone_number)}
                    </button>
                )}
            </div>
        )
    }

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    if(!dealership) {
        return null;
    }

    return (
        <>
            <header
            className={'site'}
            style={{
                backdropFilter: scrolled ? 'blur(20px)' : 'none',
                backgroundColor: 'rgba(255,255,255,0.97)',
                borderBottom: `1px solid ${scrolled ? 'transparent' : Theme.colors.border()}`,
                boxShadow: scrolled ? Theme.boxShadow.nav : 'none',
                left: 0,
                position: 'fixed',
                right: 0,
                top: 0,
                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                zIndex: 5000
            }}>
                {/* Three fixed columns rather than space-between, so the logo
                    and navigation hold their positions regardless of what the
                    right column is currently rendering. */}
                <div style={{
                    alignItems: 'center',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    margin: '0 auto',
                    maxWidth: Theme.layout.maxWidth,
                    minHeight: Theme.layout.headerHeight(),
                    padding: Theme.layout.headerPadding()
                }}>
                    {getLogo()}
                    {getNavigation()}
                    {getActions()}
                </div>
            </header>
            {getMobileMenu()}
        </>
    )
}

export default Header;
